/*
 * Profile section — `profile.section` (App-shell canon §9, FLEXIBLE).
 * Transcribed from the canon §9 table + acceptance criteria and the DC source
 * markup (App Shell.dc.html); canon wins on conflict. DOM ids per §1.3:
 * profile.root / profile.account / profile.managed / profile.addchild /
 * profile.settings. testids per the §9 table.
 *
 * DATA (Ruling A honest-empty, BOARDROOM-2026-08-09-133534, LOCKED):
 *  - Account card shows the REAL signed-in Cognito account (getProfile seam).
 *    The canon's "Alex Morgan / alex@hey.com" are [demo] placeholders only.
 *  - `Edit` opens the profile wizard (§5) at step 1 — the wizard prefills
 *    from the real account via profileStore.loadSelfDraft. This REPLACES the
 *    pre-canon flat attribute editor, removing its §11 must-not violations
 *    from this surface (single ambiguous DOB text field; free-text gender
 *    persisted).
 *  - MANAGED PROFILES: the child-record system is the V1.1 kids amendment
 *    (§12.2) — no persisted child store exists yet, so the section loads
 *    honestly EMPTY. Children added through the inline panel are the user's
 *    own typed input held in session memory (never fabricated, never seeded).
 *    // TODO(V1.1 kids §12.2): load from + persist to the child-record store
 *    // (single record store shared with the assign flow, §9 AC-secondary).
 *  - ACCOUNT rows: destinations are TBD-UNSEEN (§12.6) — Password /
 *    Two-step verification / Notifications surface the honest not-wired
 *    toast (never a silent dead-end, §9 must-not). Sign out is wired to the
 *    real Cognito hosted-UI logout.
 */
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, ComponentType } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useShellChrome } from '../components/AppShell'
import { loadSession, clearSession } from '../auth/session'
import { globalSignOut } from '../auth/cognitoAuth'
import { getProfile, type CognitoProfile } from '../auth/cognitoProfile'
import { IconBell, IconKey, IconShield, IconSignOut } from '../lib/icons'
import { showNotWiredToast } from '../lib/notWiredToast'

// ---------------------------------------------------------------------------
// Styles (canon §2 tokens; DC inline styles transcribed)
// ---------------------------------------------------------------------------

const kickerStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-faint)', margin: '0 2px 8px',
}
const cardStyle: CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 15,
}
const labelStyle: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6,
}
const inputStyle: CSSProperties = {
  width: '100%', minHeight: 46, border: '1px solid var(--hairline-14)',
  borderRadius: 11, padding: '0 13px', fontSize: 15, background: 'var(--bg-frame)',
  color: 'var(--text)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
}
/** List chevron `›` — color.text.chevron (canon §2.1). */
const chevronStyle: CSSProperties = { color: 'var(--text-chevron)', flex: 'none' }

// ---------------------------------------------------------------------------
// Managed children — V1.1 seam
// ---------------------------------------------------------------------------

interface ManagedChild {
  key: string
  first: string
  last: string
  // TODO(V1.1 kids §12.2): the full subject-scoped child profile record
  // (dobD/M/Y, gender, nationality, resCountry, city, EC, junior shirt …)
  // lives in the child-record store when the kids amendment lands.
}

/** Completeness note — DERIVED from the child's own data per canon §2.4
 *  (`pDone = first && last && dobD && dobM && dobY && gender && nationality
 *  && resCountry && city`); NEVER a stored "done" flag (§11.12). V1 children
 *  carry names only, so the note always reads "profile to finish" until the
 *  V1.1 child-record store carries the rest. */
function childNote(c: ManagedChild): string {
  // TODO(V1.1 kids §12.2): extend with the record fields —
  //   const done = !!(c.first && c.last && c.dobD && c.dobM && c.dobY &&
  //     c.gender && c.nationality && c.resCountry && c.city)
  const done = false && !!(c.first && c.last)
  return done ? 'Child · no login · profile complete' : 'Child · no login · profile to finish'
}

// ---------------------------------------------------------------------------
// Sign out — clear the local session + best-effort Cognito GlobalSignOut, then
// return to the branded in-app login. NO amazoncognito.com hop on logout
// (email-first auth has no hosted UI).
// ---------------------------------------------------------------------------

function signOut() {
  const session = loadSession()
  clearSession()
  // Best-effort revoke of the refresh/access tokens server-side; ignore failure
  // (the local session is already gone, which is what gates the app).
  if (session?.accessToken) void globalSignOut(session.accessToken).catch(() => {})
  window.location.assign('/')
}

// ---------------------------------------------------------------------------
// Account settings rows (canon §9 — labels + metas verbatim [chrome])
// ---------------------------------------------------------------------------

interface SettingsRow {
  k: 'password' | '2sv' | 'notifications' | 'signout'
  label: string
  meta: string
  Icon: ComponentType<{ size?: number }>
  onClick: () => void
}

// NOTE (PROCESS-3 flag, reported up): the `On` meta on Two-step verification
// is the canon-verbatim [chrome] string (§9 copy contract). Reading the
// account's REAL MFA state belongs with the settings destination, which is
// TBD-UNSEEN (§12.6) — until that lands this meta is canon copy, not a live
// status read. Raised as an ambiguity, not resolved silently in code.
const SETTINGS: SettingsRow[] = [
  { k: 'password', label: 'Password', meta: 'Change', Icon: IconKey, onClick: () => showNotWiredToast() },
  { k: '2sv', label: 'Two-step verification', meta: 'On', Icon: IconShield, onClick: () => showNotWiredToast() },
  { k: 'notifications', label: 'Notifications', meta: '', Icon: IconBell, onClick: () => showNotWiredToast() },
  { k: 'signout', label: 'Sign out', meta: '', Icon: IconSignOut, onClick: signOut },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Profile() {
  const { showPageHead } = useShellChrome()
  const navigate = useNavigate()
  const location = useLocation()

  // --- account card: real Cognito account, honest states while loading ---
  const [p, setP] = useState<CognitoProfile | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  useEffect(() => {
    const session = loadSession()
    if (!session) return
    let cancelled = false
    getProfile(session.accessToken)
      .then((profile) => { if (!cancelled) setP(profile) })
      .catch((e) => { if (!cancelled) setLoadError(e instanceof Error ? e.message : 'profile load failed') })
    return () => { cancelled = true }
  }, [])

  const session = loadSession()
  const name = [p?.givenName, p?.familyName].filter(Boolean).join(' ')
  const email = p?.email ?? session?.claims.email ?? ''

  // --- managed children: honest-empty; session-memory only (V1.1 seam) ---
  const [children, setChildren] = useState<ManagedChild[]>([])
  // Inline panel; the optional route /profile/children/new (canon §1.2)
  // resolves here via the /profile/* wildcard and opens the panel.
  const [adding, setAdding] = useState(location.pathname === '/profile/children/new')
  const [kidFirst, setKidFirst] = useState('')
  const [kidLast, setKidLast] = useState('')
  const seq = useRef(0)
  const kidReady = kidFirst.trim() !== '' && kidLast.trim() !== ''

  const openAddKid = () => { setAdding(true); setKidFirst(''); setKidLast('') }
  const cancelAddKid = () => { setAdding(false); setKidFirst(''); setKidLast('') }
  const saveKid = () => {
    if (!kidReady) return // inert until BOTH names (canon §9)
    seq.current += 1
    const key = `k${Date.now().toString(36)}-${seq.current}`
    setChildren((cs) => [...cs, { key, first: kidFirst.trim(), last: kidLast.trim() }])
    // TODO(V1.1 kids §12.2): persist the new child to the child-record store
    // (single record store — the assign flow reads the same records, §9 AC).
    setAdding(false)
    setKidFirst('')
    setKidLast('')
  }

  const openChildWizard = (child: ManagedChild) => {
    // Canon §9: a child row opens THAT child's wizard (subject-scoped
    // record). The child-subject wizard variant is V1.1-DEFERRED (§12.2) —
    // TODO(V1.1 kids §12.2): navigate(`/get-ready/profiles/${child.key}/step-1`)
    // Until it lands, an honest not-wired signal — never a silent dead-end.
    void child
    showNotWiredToast()
  }

  return (
    <div id="profile.root">
      {/* Desktop h1 (canon §9; page h1s do not render on mobile, §3.2) */}
      {showPageHead && (
        <h1 data-testid="profile-head" style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 18px' }}>Profile</h1>
      )}

      {/* ACCOUNT CARD — name 18/800 + muted email + outline teal Edit (§9) */}
      <div id="profile.account" data-testid="profile-account" style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 13, padding: 15, marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div data-testid="profile-name" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>
            {/* Honest fallback while attributes are absent/loading — a
                generic label, never a fabricated person name. */}
            {name || 'Your account'}
          </div>
          <div data-testid="profile-email" style={{ fontSize: 12.5, color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>{email}</div>
          {loadError && (
            <div role="alert" style={{ fontSize: 11.5, color: 'var(--error)', marginTop: 4 }}>{loadError}</div>
          )}
        </div>
        <button
          data-testid="profile-edit"
          onClick={() => navigate('/get-ready/profile/step-1')}
          style={{ flex: 'none', border: '1px solid rgba(63,224,200,.35)', borderRadius: 9, background: 'transparent', color: 'var(--accent-bright)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: '0 14px' }}
        >Edit</button>
      </div>

      {/* MANAGED PROFILES (§9) */}
      <div id="profile.managed">
        <div style={kickerStyle}>Managed profiles</div>
        {children.length > 0 ? (
          <div style={{ ...cardStyle, padding: '5px 6px', marginBottom: 11 }}>
            {children.map((c) => (
              <button
                key={c.key}
                data-testid="profile-child-row"
                data-child={c.key}
                onClick={() => openChildWizard(c)}
                style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', border: 0, background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', textAlign: 'left', padding: '12px 10px', cursor: 'pointer', minHeight: 44 }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>{`${c.first} ${c.last}`.trim()}</span>
                  <span data-testid="profile-child-note" style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)' }}>{childNote(c)}</span>
                </span>
                <span style={chevronStyle}>›</span>
              </button>
            ))}
          </div>
        ) : (
          // Honest empty state — verbatim [chrome] copy (canon §9).
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, padding: '0 2px 11px' }}>
            No one yet. Add a child to enter them into junior events — they never need their own login.
          </div>
        )}
      </div>

      {/* + ADD A CHILD — dashed affordance → inline panel (§9). Children
          never get a login: name fields ONLY, no email/password anywhere. */}
      <div id="profile.addchild">
        {!adding ? (
          <button
            data-testid="profile-add-child"
            onClick={openAddKid}
            style={{ width: '100%', minHeight: 46, border: '1.5px dashed var(--hairline-16)', borderRadius: 12, background: 'transparent', color: 'var(--text)', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', marginBottom: 22 }}
          >+ Add a child</button>
        ) : (
          <div data-testid="profile-addchild-panel" style={{ background: 'var(--surface)', border: '1px solid var(--hairline-12)', borderRadius: 15, padding: '15px 16px', marginBottom: 22 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, marginBottom: 4 }}>Add a child</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 14 }}>
              Their name now — date of birth, category and kit come with their first event.
            </div>
            <label style={labelStyle}>First name</label>
            <input data-testid="profile-kid-first" value={kidFirst} onChange={(e) => setKidFirst(e.target.value)} placeholder="Leo" style={inputStyle} />
            <div style={{ height: 12 }} />
            <label style={labelStyle}>Last name</label>
            <input data-testid="profile-kid-last" value={kidLast} onChange={(e) => setKidLast(e.target.value)} placeholder="Morgan" style={inputStyle} />
            <div style={{ display: 'flex', gap: 9, marginTop: 15 }}>
              <button
                data-testid="profile-kid-cancel"
                onClick={cancelAddKid}
                style={{ flex: 'none', border: '1px solid var(--hairline-16)', borderRadius: 11, minHeight: 44, padding: '0 14px', background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >Cancel</button>
              <button
                data-testid="profile-kid-save"
                onClick={saveKid}
                aria-disabled={!kidReady}
                style={{ flex: 1, border: 0, borderRadius: 11, minHeight: 44, background: 'var(--accent)', color: 'var(--accent-ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, ...(kidReady ? { cursor: 'pointer' } : { opacity: 0.35, cursor: 'not-allowed' }) }}
              >Add child</button>
            </div>
          </div>
        )}
      </div>

      {/* ACCOUNT settings rows (§9) — Sign out wired; the rest honest
          not-wired (destinations TBD-UNSEEN §12.6, never a silent dead-end). */}
      <div id="profile.settings">
        <div style={kickerStyle}>Account</div>
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          {SETTINGS.map((s) => (
            <button
              key={s.k}
              data-testid="profile-settings-row"
              data-k={s.k}
              onClick={s.onClick}
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', border: 0, background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', textAlign: 'left', padding: '14px 15px', minHeight: 44, cursor: 'pointer', borderBottom: '1px solid var(--hairline-06)' }}
            >
              <span aria-hidden style={{ flex: 'none', display: 'inline-flex', color: 'var(--text-secondary)' }}><s.Icon size={16} /></span>
              <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{s.label}</span>
              {s.meta && <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>{s.meta}</span>}
              <span style={chevronStyle}>›</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
